import type { ConfigSource, ConfigValue } from '@/types/config';
import { supabase } from '@/integrations/supabase/client';

enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3
}

interface WindowEnv {
  __ENV__?: Record<string, string>;
  __CONFIG__?: Record<string, string>;
}

class ConfigLoader {
  private static instance: ConfigLoader;
  private cache: Map<string, ConfigValue> = new Map();
  private clientExposedKeys: Set<string> = new Set();
  private logLevel: LogLevel = LogLevel.INFO;
  private isLoading: boolean = false;
  private loadPromise: Promise<void> | null = null;

  private constructor() {
    // Define which keys should be exposed to the client
    this.clientExposedKeys = new Set([
      'GA_TRACKING_ID',
      'CLARITY_PROJECT_ID',
      'POSTHOG_API_KEY',
      'POSTHOG_HOST_URL',
      'CHATWOOT_WEBSITE_TOKEN',
      'CHATWOOT_BASE_URL'
    ]);
  }

  public static getInstance(): ConfigLoader {
    if (!ConfigLoader.instance) {
      ConfigLoader.instance = new ConfigLoader();
    }
    return ConfigLoader.instance;
  }

  private log(level: LogLevel, message: string, ...args: unknown[]) {
    if (level <= this.logLevel) {
      switch (level) {
        case LogLevel.ERROR:
          console.error(message, ...args);
          break;
        case LogLevel.WARN:
          console.warn(message, ...args);
          break;
        case LogLevel.INFO:
          console.log(message, ...args);
          break;
        case LogLevel.DEBUG:
          console.debug(message, ...args);
          break;
      }
    }
  }

  public setLogLevel(level: LogLevel) {
    this.logLevel = level;
  }

  private logConfigValue(key: string, value: string, source: ConfigSource) {
    const maskedValue = this.maskSensitiveValue(value);
    this.log(LogLevel.DEBUG, `Config [${source}]: ${key} = ${maskedValue}`);
  }

  private maskSensitiveValue(value: string): string {
    if (value.match(/^(G-|phx_|[A-Za-z0-9]{20,})/)) {
      return '***' + value.slice(-4);
    }
    return value;
  }

  private isClient(): boolean {
    return typeof window !== 'undefined';
  }

  public async loadRemoteConfig(): Promise<void> {
    if (this.isLoading) {
      return this.loadPromise!;
    }

    this.isLoading = true;
    this.loadPromise = (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('get-config-public');

        if (error) throw error;

        if (data) {
          // Filter out non-client-exposed keys when in browser context
          if (this.isClient()) {
            Object.entries(data).forEach(([key, value]) => {
              if (this.clientExposedKeys.has(key)) {
                this.cache.set(key, { value: value as string, source: 'remote' });
                this.logConfigValue(key, value as string, 'remote');
              }
            });
          } else {
            Object.entries(data).forEach(([key, value]) => {
              this.cache.set(key, { value: value as string, source: 'remote' });
              this.logConfigValue(key, value as string, 'remote');
            });
          }
        }
      } catch (error) {
        this.log(LogLevel.ERROR, `Error loading remote config: ${error}`);
      } finally {
        this.isLoading = false;
        this.loadPromise = null;
      }
    })();

    return this.loadPromise;
  }

  public async getValue(key: string, defaultValue?: string): Promise<string> {
    // In browser context, silently return empty string for non-client keys
    if (this.isClient() && !this.clientExposedKeys.has(key)) {
      return '';
    }

    // Check cache first
    if (this.cache.has(key)) {
      const cached = this.cache.get(key)!;
      this.logConfigValue(key, cached.value, cached.source);
      return cached.value;
    }

    // Check if this key should be exposed to client
    const shouldExposeToClient = this.clientExposedKeys.has(key);
    const envKey = shouldExposeToClient ? `VITE_${key}` : key;

    // Try environment variables first
    let envValue;
    try {
      // Try import.meta.env first (Vite's way)
      envValue = import.meta.env[envKey];
      // If not found and we're in a browser, try window.__ENV__
      if (!envValue && this.isClient()) {
        envValue = (window as WindowEnv).__ENV__?.[envKey];
      }
    } catch (e) {
      // If import.meta.env is not available, try window.__ENV__
      if (this.isClient()) {
        envValue = (window as WindowEnv).__ENV__?.[envKey];
      }
    }

    if (envValue) {
      const value = envValue as string;
      this.cache.set(key, { value, source: 'env' });
      this.logConfigValue(key, value, 'env');
      return value;
    }

    // Finally, use default value if provided
    if (defaultValue !== undefined) {
      this.cache.set(key, { value: defaultValue, source: 'default' });
      this.logConfigValue(key, defaultValue, 'default');
      return defaultValue;
    }

    this.log(LogLevel.DEBUG, `No configuration value found for key: ${key}`);
    return '';
  }

  public updateRemoteConfig(config: Record<string, string>) {
    // Filter out non-client-exposed keys when in browser context
    if (this.isClient()) {
      const filteredConfig: Record<string, string> = {};
      Object.entries(config).forEach(([key, value]) => {
        if (this.clientExposedKeys.has(key)) {
          filteredConfig[key] = value;
        }
      });
      (window as WindowEnv).__CONFIG__ = filteredConfig;
    } else {
      (window as WindowEnv).__CONFIG__ = config;
    }
    this.cache.clear();
  }

  public async hasValue(key: string): Promise<boolean> {
    const value = await this.getValue(key);
    return value !== '';
  }

  public getAllConfig(): Record<string, string> {
    const config: Record<string, string> = {};
    this.cache.forEach((value, key) => {
      if (!this.isClient() || this.clientExposedKeys.has(key)) {
        config[key] = value.value;
      }
    });
    return config;
  }
}

export const configLoader = ConfigLoader.getInstance(); 